import { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import useAxios from '../api/axios';

function formatLocation(course) {
  const municipality = String(course?.municipality || '').trim();
  const region = String(course?.region || '').trim();

  if (municipality && region) {
    return `${municipality}, ${region}`;
  }

  return municipality || region || '-';
}

function formatSharePercentage(value, total) {
  const rounds = Number(value || 0);
  const roundsTotal = Number(total || 0);

  if (roundsTotal <= 0) {
    return '0.00 %';
  }

  return `${((rounds / roundsTotal) * 100).toFixed(2)} %`;
}

function CoursesStats() {
  const api = useAxios();
  const [courseStats, setCourseStats] = useState([]);
  const [availableYears, setAvailableYears] = useState([]);
  const [selectedYear, setSelectedYear] = useState(null);
  const [loading, setLoading] = useState(true);
  const totalRounds = courseStats.reduce((sum, row) => sum + Number(row?.playedRounds || 0), 0);

  useEffect(() => {
    let isMounted = true;

    const loadCourseStats = async () => {
      try {
        setLoading(true);

        const payload = selectedYear ? { year: Number(selectedYear) } : {};
        const response = await api.post(
          '/getStats',
          JSON.stringify(payload),
          { headers: { 'Content-Type': 'application/json' } }
        );

        if (!isMounted) {
          return;
        }

        const responseYear = Number(response?.data?.year) || new Date().getFullYear();
        setCourseStats(Array.isArray(response?.data?.courseStats) ? response.data.courseStats : []);
        setAvailableYears(Array.isArray(response?.data?.availableYears) ? response.data.availableYears : []);

        if (!selectedYear) {
          setSelectedYear(responseYear);
        }
      } catch (err) {
        if (!isMounted) {
          return;
        }

        const message = err?.response?.data?.message || err?.response?.data?.msg;
        toast.error(message || 'Ratatilastojen haku epäonnistui');
        setCourseStats([]);
        setAvailableYears([]);
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    loadCourseStats();

    return () => {
      isMounted = false;
    };
  }, [selectedYear]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="mx-auto max-w-5xl px-4 pb-10 pt-24">
      <div className='mb-6 rounded-[28px] border border-white/10 bg-slate-900/70 p-5 shadow-2xl shadow-slate-950/30 backdrop-blur md:flex md:items-end md:justify-between'>
        <div>
          <p className="text-sm font-semibold uppercase tracking-[0.35em] text-cyan-300">Radat</p>
          <h1 className="mt-2 text-3xl font-black text-white md:text-4xl">Pelatut kierrokset radoittain</h1>
          <p className="mt-2 text-sm text-slate-300">Listaus on järjestetty suurimmasta pienimpään pelattujen kierrosten mukaan.</p>
        </div>

        <div className="mt-4 md:mt-0">
          <label className="block text-xs font-semibold uppercase tracking-[0.25em] text-cyan-300" htmlFor="course-stats-year">
            Vuosi
          </label>
          <select
            id="course-stats-year"
            value={selectedYear ?? ''}
            onChange={(event) => setSelectedYear(Number(event.target.value))}
            className="mt-2 rounded-xl border border-white/10 bg-white/5 px-4 py-2 text-sm text-white outline-none transition focus:border-cyan-300"
            disabled={loading || availableYears.length === 0}
          >
            {availableYears.length === 0 ? <option value="">Ei vuosia</option> : null}
            {availableYears.map((year) => (
              <option key={year} value={year} className="bg-slate-900 text-white">
                {year}
              </option>
            ))}
          </select>
        </div>
      </div>

      {loading ? (
        <div className='rounded-2xl border border-dashed border-white/15 bg-white/5 px-6 py-10 text-center text-slate-300'>
          <p className="text-lg font-semibold text-white">Ladataan ratatilastoja...</p>
        </div>
      ) : courseStats.length === 0 ? (
        <div className='rounded-2xl border border-dashed border-white/15 bg-white/5 px-6 py-10 text-center text-slate-300'>
          <p className="text-lg font-semibold text-white">Ratatilastoja ei ole vielä saatavilla.</p>
        </div>
      ) : (
        <div className="overflow-hidden rounded-[28px] border border-white/10 bg-slate-900/70 shadow-2xl shadow-slate-950/30 backdrop-blur">
          <div className="overflow-x-auto">
            <table className="min-w-full text-left text-sm text-slate-200">
              <thead className="bg-white/5 text-xs uppercase tracking-[0.2em] text-cyan-300">
                <tr>
                  <th className="px-4 py-4">#</th>
                  <th className="px-4 py-4">Rata</th>
                  <th className="px-4 py-4">Sijainti</th>
                  <th className="px-4 py-4 text-right">Kierroksia</th>
                  <th className="px-4 py-4 text-right">Osuus</th>
                </tr>
              </thead>
              <tbody>
                {courseStats.map((row, index) => (
                  <tr key={row.courseID || `${row.name}-${index}`} className="border-t border-white/10">
                    <td className="px-4 py-4 font-black text-white">{index + 1}</td>
                    <td className="px-4 py-4 font-semibold text-white">{row.name}</td>
                    <td className="px-4 py-4 text-slate-300">{formatLocation(row)}</td>
                    <td className="px-4 py-4 text-right font-black text-emerald-300">{Number(row.playedRounds || 0)}</td>
                    <td className="px-4 py-4 text-right font-black text-cyan-300">{formatSharePercentage(row.playedRounds, totalRounds)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

export default CoursesStats;
